import { signal } from '@preact/signals';
import type { EpisodeDto } from '../../shared/types';
import type { DownloadRow, PodcastieNative } from './native';
import { onNativeEvent, whenNative } from './native';

/**
 * Offline downloads, as offered by the Android app.
 *
 * The browser cannot do this: audio lives on the publisher's CDN, and caching it in a service
 * worker would break the Range requests seeking depends on (see `public/sw.js`). The Android
 * shell downloads episodes natively instead and exposes a bridge on `window.PodcastieNative`;
 * everything here is a thin, optional client for it. With no bridge present — every browser, and
 * the iOS PWA — `hasNative` stays false and the UI simply never renders a download control.
 *
 * Deliberately free of DOM types — see the note in `test/downloads-state.test.ts`.
 */

export type { DownloadRow, DownloadStatus } from './native';
// Re-exported from where the detection now lives, so the components that gate on it are
// unaffected by which feature happens to find the bridge first.
export { hasNative } from './native';

export const downloads = signal<Record<string, DownloadRow>>({});

/** How often to reconcile while something is in flight, if a push event goes astray. */
const POLL_MS = 2000;

/** Consecutive failed reconciles before we stop asking. A dead bridge never recovers on its own. */
const MAX_POLL_FAILURES = 5;

let bridge: PodcastieNative | null = null;
let poll: ReturnType<typeof setInterval> | null = null;
let started = false;
let pollFailures = 0;

/**
 * Called once at boot. Safe, and a no-op, everywhere the native shell is absent.
 *
 * Guarded against re-entry: `onNativeEvent` has no unsubscribe, so a second call would attach a
 * second listener and apply every push twice. HMR and the test suite both re-enter it.
 */
export function initDownloads(): void {
  if (started) return;
  whenNative((native) => {
    if (started) return;
    started = true;
    bridge = native;
    pollFailures = 0;
    onNativeEvent((event) => {
      if (event?.type === 'download' && event.row) apply(event.row);
    });
    void refresh();
  });
}

/**
 * Test seam: drops this module's state so each case starts from nothing.
 *
 * The shared bridge is `resetNative`'s job and the caller's to invoke, the same way `resetCast`
 * leaves it alone. Calling it from here would clear the detection and the event subscription
 * `cast.ts` is relying on while leaving that module's own `started` flag set, which silently
 * disables casting for the rest of the page's life.
 */
export function resetDownloads(): void {
  bridge = null;
  started = false;
  pollFailures = 0;
  downloads.value = {};
  stopPolling();
}

async function refresh(): Promise<void> {
  if (!bridge) return;
  try {
    const rows = await bridge.list();
    const next: Record<string, DownloadRow> = {};
    for (const row of rows) next[row.episodeId] = row;
    downloads.value = next;
    pollFailures = 0;
  } catch {
    // A failed list is not worth surfacing on its own; the next event or poll corrects it. But a
    // bridge that has genuinely died — a reply proxy invalidated by a document swap — never
    // recovers by itself, and a stuck QUEUED row would otherwise keep the 2s poll alive for the
    // life of the page.
    pollFailures++;
  }
  syncPolling();
}

export function downloadOf(episodeId: number | string): DownloadRow | undefined {
  return downloads.value[String(episodeId)];
}

export function isDownloaded(episodeId: number | string): boolean {
  return downloadOf(episodeId)?.status === 'COMPLETE';
}

export async function startDownload(episode: EpisodeDto): Promise<void> {
  if (!bridge) return;
  // Everything the native side needs to browse and play this episode with no network at all —
  // it cannot call the API to fill in the gaps later.
  const spec = {
    id: String(episode.id),
    feedId: episode.feedId == null ? null : String(episode.feedId),
    title: episode.title,
    feedTitle: episode.feedTitle ?? '',
    imageUrl: episode.imageUrl,
    feedImageUrl: episode.feedImageUrl,
    enclosureUrl: episode.enclosureUrl,
    durationSec: episode.durationSec ?? 0,
    pubDate: episode.pubDate ?? 0,
    positionSec: episode.progress?.positionSec ?? 0,
    completed: episode.progress?.completed ?? false,
  };

  // Optimistic, so the button responds to the tap rather than to the round trip.
  apply({
    episodeId: spec.id,
    status: 'QUEUED',
    bytesDone: 0,
    bytesTotal: 0,
    title: episode.title,
  });

  try {
    const row = await bridge.download(spec);
    // Only if nothing better arrived while we were waiting: a push event carrying RUNNING (or
    // COMPLETE, for a small file) can land during the await, and the row this call returns is
    // by then the stale one.
    if (row && downloadOf(spec.id)?.status === 'QUEUED') {
      apply(row);
    } else if (!row) {
      // The native side declined — too little free space is the usual reason. Drop the
      // optimistic row so the button returns to Download rather than showing a spinner that
      // will never move.
      forget(spec.id);
    }
  } catch {
    // The call failed, but the native side may well have enqueued it anyway — dropping the row
    // here would revert the button and let a second tap start it twice. Ask instead of guessing.
    void refresh();
  }
}

export async function cancelDownload(episodeId: number | string): Promise<void> {
  if (!bridge) return;
  const id = String(episodeId);
  try {
    await bridge.cancel(id);
  } finally {
    forget(id);
  }
}

export async function removeDownload(episodeId: number | string): Promise<void> {
  if (!bridge) return;
  const id = String(episodeId);
  try {
    await bridge.remove(id);
  } finally {
    forget(id);
  }
}

export async function removeAllDownloads(): Promise<void> {
  if (!bridge) return;
  await bridge.removeAll();
  downloads.value = {};
  stopPolling();
}

export async function storageUsage(): Promise<{ usedBytes: number; freeBytes: number } | null> {
  if (!bridge) return null;
  try {
    return await bridge.usage();
  } catch {
    return null;
  }
}

export async function getWifiOnly(): Promise<boolean> {
  if (!bridge) return true;
  try {
    return await bridge.getWifiOnly();
  } catch {
    return true;
  }
}

export async function setWifiOnly(value: boolean): Promise<boolean> {
  if (!bridge) return true;
  try {
    return await bridge.setWifiOnly(value);
  } catch {
    // Report what the setting actually is, rather than throwing into an onChange handler.
    return getWifiOnly();
  }
}

// ------------------------------------------------------------------ internals

function apply(row: DownloadRow): void {
  // A push arriving means the bridge is alive after all, so polling deserves another chance.
  pollFailures = 0;
  if (row.status === 'CANCELLED') {
    forget(row.episodeId);
    return;
  }
  downloads.value = { ...downloads.value, [row.episodeId]: row };
  syncPolling();
}

function forget(episodeId: string): void {
  const next = { ...downloads.value };
  delete next[episodeId];
  downloads.value = next;
  syncPolling();
}

function isActive(row: DownloadRow): boolean {
  return row.status === 'QUEUED' || row.status === 'RUNNING';
}

/**
 * Polls only while something is actually downloading. Push events are the primary channel — this
 * exists so a frame lost across a document swap cannot leave a progress bar stuck forever — and
 * an unconditional interval is exactly the thing that drains a phone in a pocket.
 */
function syncPolling(): void {
  const active = Object.values(downloads.value).some(isActive);
  if (!active || pollFailures >= MAX_POLL_FAILURES) {
    stopPolling();
    return;
  }
  if (!poll) poll = setInterval(() => void refresh(), POLL_MS);
}

function stopPolling(): void {
  if (poll === null) return;
  clearInterval(poll);
  poll = null;
}
